import React from 'react';

// Shared subscription status helpers used by both the Profile summary and the
// Billing page so status/trial display stays consistent.

const STATUS_CLASSES: Record<string, string> = {
  active: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
  trialing: 'bg-blue-500/15 text-blue-300 border-blue-500/30',
  past_due: 'bg-amber-500/15 text-amber-300 border-amber-500/30',
  canceled: 'bg-red-500/15 text-red-300 border-red-500/30',
  incomplete: 'bg-white/10 text-white/60 border-white/15',
  unpaid: 'bg-red-500/15 text-red-300 border-red-500/30',
};

const STATUS_LABELS: Record<string, string> = {
  active: 'Active',
  trialing: 'Trial',
  past_due: 'Past due',
  canceled: 'Canceled',
  incomplete: 'Incomplete',
  unpaid: 'Unpaid',
};

export function statusLabel(status?: string | null): string {
  if (!status) return '';
  return STATUS_LABELS[status] ?? status.replace(/_/g, ' ');
}

export function StatusPill({ status }: { status: string | null }) {
  if (!status) return null;
  const cls = STATUS_CLASSES[status] ?? 'bg-white/10 text-white/60 border-white/15';
  return (
    <span className={`px-2 py-0.5 text-xs rounded-full border ${cls}`}>
      {statusLabel(status)}
    </span>
  );
}

/** Whole days from now until `iso`, clamped at 0. Null if unparseable. */
export function daysUntil(iso?: string | null): number | null {
  if (!iso) return null;
  const end = new Date(iso).getTime();
  if (Number.isNaN(end)) return null;
  return Math.max(0, Math.ceil((end - Date.now()) / 86_400_000));
}

/** "5 days left in trial" pill — only renders while the plan is trialing. */
export function TrialBadge({
  status,
  trialEnd,
  currentPeriodEnd,
}: {
  status: string | null;
  // Trial end date. During a trial Stripe leaves current_period_end null, so
  // prefer trialEnd and only fall back to currentPeriodEnd for older data.
  trialEnd?: string | null;
  currentPeriodEnd?: string | null;
}) {
  if (status !== 'trialing') return null;
  const d = daysUntil(trialEnd ?? currentPeriodEnd);
  if (d == null) return null;
  const label = d === 0 ? 'Trial ends today' : d === 1 ? '1 day left in trial' : `${d} days left in trial`;
  return (
    <span className="px-2 py-0.5 text-xs rounded-full bg-blue-500/15 text-blue-300 border border-blue-500/30">
      {label}
    </span>
  );
}
